import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import logo from "../buckland.png";
import Breadcrumbs from "./Breadcrumbs";

const ClassificationDetails = () => {
  const { item_mx_hts_code } = useParams();
  const [classificationData, setClassificationData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const breadcrumbs = [
    { label: "Item Catalogue", path: "/customerItemCatalogueGrid" },
    { label: `Classification Code: ${item_mx_hts_code}` },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");

        // Updated to use the backend API instead of direct external API call
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/classification_details/tariff`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            params: {
              item_mx_hts_code,
            },
          }
        );

        setClassificationData(response.data[0]); // Assuming response.data is an array
      } catch (err) {
        setError("Failed to fetch data: " + err.message);
        console.error("API Error:", err);
      }
      setLoading(false);
    };
    fetchData();
  }, [item_mx_hts_code]);

  const renderTableRow = (label, key) => {
    const value = classificationData[key] || "Not available";
    return (
      <tr className="border-b border-gray-100">
        <td className="px-4 py-2 font-semibold w-1/3">{label}</td>
        <td
          className="px-4 py-2 text-gray-600 w-2/3 overflow-hidden"
          style={{ maxHeight: "50px" }}
        >
          {value}
        </td>
      </tr>
    );
  };

  const fields = [
    { label: "HTS Code", key: "item_mx_hts_code" },
    { label: "HTS Description", key: "mx_hts_desc" },
    { label: "Additional HTS Description", key: "mx_hts_desc2" },
    { label: "Chapter", key: "mx_hts_capitulo" },
    { label: "Chapter Description", key: "mx_hts_capitulo_desc" },
    { label: "Chapter Additional Description 1", key: "mx_hts_capitulo_desc1" },
    { label: "Chapter Additional Description 2", key: "mx_hts_capitulo_desc2" },
    { label: "Heading", key: "mx_hts_partida" },
    { label: "Heading Description 1", key: "mx_hts_partida_desc1" },
    { label: "Heading Description 2", key: "mx_hts_partida_desc2" },
    { label: "Subheading", key: "mx_hts_subpartida" },
    { label: "Subheading Description 1", key: "mx_hts_subpartida_desc1" },
    { label: "Subheading Description 2", key: "mx_hts_subpartida_desc2" },
    { label: "Section", key: "mx_hts_seccion" },
    { label: "Section Description", key: "mx_hts_seccion_desc" },
    { label: "NICO", key: "mx_hts_nico" },
    { label: "NICO Description", key: "mx_hts_nico_desc" },
    { label: "Unit of Measurement", key: "mx_hts_umt" },
  ];

  const renderTextCard = (label, keys) => {
    const texts = keys
      .map((key) => classificationData[key])
      .filter((text) => text && text.trim() !== "") // Filtering out empty or whitespace-only strings
      .join(" ");
    if (!texts) return null; // Do not render the card if there is no text
    return (
      <div className="bg-white p-6 shadow-xl rounded-lg mb-6 transition duration-300 ease-in-out hover:shadow-2xl">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">{label}</h3>
        <textarea
          readOnly
          className="w-full h-32 p-4 border-0 rounded-lg bg-gray-100 text-gray-700 resize-none"
          value={texts}
        ></textarea>
      </div>
    );
  };

  const renderDataSection = (title, dataObj) => {
    if (!dataObj) return null; // Don't render if data is not available

    return (
      <div className="bg-white p-4 shadow-md rounded-lg mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">{title}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
          {Object.entries(dataObj).map(([key, value]) => (
            <div key={key} className="p-2 bg-gray-100 rounded shadow-sm">
              <strong className="font-semibold text-sm">{key}:</strong>
              <span className="ml-1 text-xs text-gray-600">
                {value || "N/A"}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderHeader = () => (
    <div
      className="flex justify-between items-center px-4 py-3"
      style={{ backgroundColor: "#1A2654" }}
    >
      <img src={logo} alt="Company Logo" className="h-16" />
      <h1 className="text-xl font-semibold text-white">
        Classification and Tariff Information{" "}
      </h1>
    </div>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;
  if (!classificationData) return <div>No data available.</div>;

  const { mx_hts_taxes, mx_hts_preference_TL, mx_hts_preference_PS } =
    classificationData;

  return (
    <>
      {renderHeader()}
      <div className="mx-auto px-4 py-8 bg-gray rounded-lg shadow-lg">
        <Breadcrumbs crumbs={breadcrumbs} />

        <div className="mb-5">
          <h2 className="text-3xl font-bold text-gray-800 mb-3">
            Classification Details
          </h2>
          <div className="h-1 bg-gradient-to-r from-blue-500 to-green-500 rounded-full"></div>
        </div>
        <div className="grid grid-cols-2 gap-5">
          <div>
            <table className="min-w-full">
              <tbody>
                {fields
                  .slice(0, 9)
                  .map((field) => renderTableRow(field.label, field.key))}
              </tbody>
            </table>
          </div>
          <div>
            <table className="min-w-full">
              <tbody>
                {fields
                  .slice(9)
                  .map((field) => renderTableRow(field.label, field.key))}
              </tbody>
            </table>
          </div>
        </div>
        {renderDataSection("HTS Taxes", mx_hts_taxes)}
        {renderDataSection("HTS Preference TL", mx_hts_preference_TL)}
        {renderDataSection("HTS Preference PS", mx_hts_preference_PS)}
        {renderTextCard("HTS Restrictions", [
          "mx_hts_restrictionsi1",
          "mx_hts_restrictionsi2",
          "mx_hts_restrictionsi3",
          "mx_hts_restrictionsi4",
          "mx_hts_restrictionsi5",
          "mx_hts_restrictionsi6",
          "mx_hts_restrictionsi7",
          "mx_hts_restrictionsi8",
          "mx_hts_restrictionsi9",
          "mx_hts_restrictionsi10",
          "mx_hts_restrictionsi11",
          "mx_hts_restrictionsi12",
          "mx_hts_restrictionsi13",
          "mx_hts_restrictionsi14",
          "mx_hts_restrictionsi15",
          "mx_hts_restrictionsi16",
          "mx_hts_restrictionsi17",
          "mx_hts_restrictionsi18",
          "mx_hts_restrictionsi19",
          "mx_hts_restrictionsi20",
          "mx_hts_restrictionsi21",
          "mx_hts_restrictionsi22",
          "mx_hts_restrictionsi23",
          "mx_hts_restrictionsi24",
          "mx_hts_restrictionsi25",
        ])}
        {renderTextCard("Extended HTS Restrictions", [
          "mx_hts_restrictionse1",
          "mx_hts_restrictionse2",
          "mx_hts_restrictionse3",
          "mx_hts_restrictionse4",
          "mx_hts_restrictionse5",
          "mx_hts_restrictionse6",
          "mx_hts_restrictionse7",
          "mx_hts_restrictionse8",
          "mx_hts_restrictionse9",
        ])}
        {renderTextCard("Extended HTS Payment", [
          "mx_hts_textpai1",
          "mx_hts_textpai2",
          "mx_hts_textpai3",
          "mx_hts_textpai4",
          "mx_hts_textpai5",
          "mx_hts_textpai6",
          "mx_hts_textpai7",
          "mx_hts_textpai8",
          "mx_hts_textpai9",
        ])}
        {renderTextCard("HTS Import Texts", [
          "mx_hts_textimp1",
          "mx_hts_textimp2",
          "mx_hts_textimp3",
          "mx_hts_textimp4",
          "mx_hts_textimp5",
          "mx_hts_textimp6",
          "mx_hts_textimp7",
          "mx_hts_textimp8",
          "mx_hts_textimp9",
        ])}
        {renderTextCard("HTS CUOTAS", [
          "mx_hts_CUOTAS1",
          "mx_hts_CUOTAS2",
          "mx_hts_CUOTAS3",
          "mx_hts_CUOTAS4",
          "mx_hts_CUOTAS5",
          "mx_hts_CUOTAS6",
          "mx_hts_CUOTAS7",
          "mx_hts_CUOTAS8",
          "mx_hts_CUOTAS9",
        ])}
      </div>
    </>
  );
};

export default ClassificationDetails;
